import type { FC } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { differenceInCalendarDays, isSameMonth, isToday, parseISO } from 'date-fns';
import ReactCountryFlag from 'react-country-flag';
import type { GoogleCreativeRegionStat } from '../api/types';

type RecencyTier = 'today' | 'week' | 'month' | 'old';

const getRecencyTier = (dateStr: string): RecencyTier => {
  const date = parseISO(dateStr);
  const now = new Date();
  if (isToday(date)) return 'today';
  const daysDiff = differenceInCalendarDays(now, date);
  if (daysDiff <= 7) return 'week';
  if (isSameMonth(date, now)) return 'month';
  return 'old';
};

const TIER_COLORS: Record<RecencyTier, string> = {
  today: '#E8F5E9',  // green tint
  week: '#FFF3E0',   // orange tint
  month: '#FFFDE7',  // yellow tint
  old: '#F5F5F5',    // grey tint
};

const TIER_DOT_COLORS: Record<RecencyTier, string> = {
  today: '#2E7D32',
  week: '#E65100',
  month: '#F9A825',
  old: '#9E9E9E',
};

interface GeoPanelRowProps {
  entry: GoogleCreativeRegionStat;
}

const GeoPanelRow: FC<GeoPanelRowProps> = ({ entry }) => {
  const tier = getRecencyTier(entry.lastShownOn);
  const bgColor = TIER_COLORS[tier];
  const dotColor = TIER_DOT_COLORS[tier];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.75,
        bgcolor: bgColor,
        borderRadius: 1,
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: dotColor,
            flexShrink: 0,
          }}
        />
        <ReactCountryFlag
          countryCode={entry.regionCode}
          svg
          style={{ width: '1.25em', height: '1.25em', flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {entry.regionName}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
      >
        {entry.lastShownOn}
      </Typography>
    </Box>
  );
};

interface GeoPanelLegendItemProps {
  color: string;
  label: string;
}

const GeoPanelLegendItem: FC<GeoPanelLegendItemProps> = ({ color, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

interface GoogleGeoPanelProps {
  regionStats: GoogleCreativeRegionStat[];
}

export const GoogleGeoPanelSkeleton: FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
    ))}
  </Box>
);

const GoogleGeoPanel: FC<GoogleGeoPanelProps> = ({ regionStats }) => {
  if (!regionStats.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No geo data available
      </Typography>
    );
  }

  const sortedRegionStats = [...regionStats].sort(
    (a, b) => parseISO(b.lastShownOn).getTime() - parseISO(a.lastShownOn).getTime()
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.75,
          px: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Country
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Last shown
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {sortedRegionStats.map((entry) => (
          <GeoPanelRow key={entry.regionCode} entry={entry} />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5, px: 0.5 }}>
        <GeoPanelLegendItem color={TIER_DOT_COLORS.today} label="Today" />
        <GeoPanelLegendItem color={TIER_DOT_COLORS.week} label="Prev 7 days" />
        <GeoPanelLegendItem color={TIER_DOT_COLORS.month} label="This month" />
        <GeoPanelLegendItem color={TIER_DOT_COLORS.old} label="Older" />
      </Box>
    </Box>
  );
};

export default GoogleGeoPanel;
